import { ErrorMessage } from "@hookform/error-message";
import { AxiosError } from "axios";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { getPasswordErrorMessages } from "../../helpers/password-errors";
import { toastSuccessMessage } from "../../utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "../../utils/amplitude";
import { rollbarAndLogError } from "../../utils/logger";

import { serverSetPassword } from "api/api-server-actions";
import { PasswordForm } from "components/PasswordForm";
import { ErrorMessageBanner } from "components/forms/ErrorMessageBanner";
import { type SetPasswordFormValue } from "components/materials/sidebar/UserProfileSidebarPanel";
import { FormattedValidationError } from "components/server-types";
import { t } from "i18n/i18n";
import { PasswordErrorKey } from "i18n/i18next";
import { LegacyButton } from "mds/components/LegacyButton";

interface ResetPasswordModalProps {
  onClose: () => void;
}

export const ResetPasswordModal = ({ onClose }: ResetPasswordModalProps) => {
  const form = useForm<SetPasswordFormValue>();
  const currentPassword = form.watch("current_password");

  const onSubmit: SubmitHandler<SetPasswordFormValue> = (formData) => {
    serverSetPassword(formData)
      .then(() => {
        toastSuccessMessage(t("success.toasts.update_success", { item: t("glossary.password") }));
        onClose();
      })
      .catch((err: unknown) => {
        if (!(err instanceof AxiosError)) {
          rollbarAndLogError("serverSetPassword", err);
          return;
        }

        const respData = err.response?.data as FormattedValidationError<PasswordErrorKey>;

        if (!respData) {
          rollbarAndLogError("serverSetPassword", err);
          return;
        }

        for (const [field, { details, t: translationKeys }] of Object.entries(respData)) {
          form.setError(field as keyof SetPasswordFormValue, {
            type: "server",
            message: getPasswordErrorMessages(translationKeys, details),
          });
        }
      });
  };

  return (
    <FormProvider {...form}>
      <form
        className="pt-5"
        onSubmit={form.handleSubmit((data) => {
          trackEvent("Account settings - Update password", BUTTON_EVENT_CATEGORY);
          onSubmit(data);
        })}
      >
        <div className="items-first flex flex-col gap-3">
          <ErrorMessage name="root" />

          <label className="body-s">
            <div>{t("user_info.current_password")}</div>

            <input
              {...form.register("current_password", {
                required: t("error.field_required"),
              })}
              aria-invalid={form.formState.errors.current_password ? "true" : "false"}
              autoComplete="current_password"
              className="mds-text-field body-m w-full text-black-tint-20"
              id="current_password"
              type="password"
            />

            <ErrorMessageBanner className="body-s mt-1" name="current_password" />
          </label>

          <PasswordForm currentPassword={currentPassword} />

          {/* This invisible field is required for LastPass to recognize the autofill */}
          <input className="m-0 hidden h-0 border-none p-0" type="submit" value="Signup" />
        </div>

        <div className="flex justify-end gap-2 pt-8">
          <LegacyButton kind="secondary" size="xs" title={t("common.cancel")} onClick={onClose}>
            {t("common.cancel")}
          </LegacyButton>

          <LegacyButton
            kind="primary"
            size="xs"
            title={t("user_info.update_password")}
            type="submit"
          >
            {t("user_info.update_password")}
          </LegacyButton>
        </div>
      </form>
    </FormProvider>
  );
};
